// ponytail: module-level stack of open Modal/Drawer overlays so that Escape
// closes only the topmost one (a Modal stacked on a Drawer must not close the
// Drawer underneath). Modal and Drawer register plain bubble-phase document
// listeners and react only while they are on top.
//
// Ein geöffnetes Dropdown-/MultiSelect-Menü steht als `kind: 'menu'` obenauf
// (registerMenuEscape): solange es offen ist, sind Drawer und Modal nicht oben,
// das erste Escape schließt nur das Menü — vorher schloss es das Menü UND den
// Editor samt ungespeicherter Eingaben. Ein Menü sperrt weder den Body-Scroll,
// noch zählt es für die Tiefe (z-Index) oder für `useModalOpen`.
//
// Der Body-Scroll wird HIER gezählt (nicht je Overlay): gesperrt, wenn das erste
// Overlay aufgeht; beim Schließen des letzten kommt der URSPRÜNGLICHE Wert zurück,
// in jeder Schließ-Reihenfolge (Drawer zu, während ein Modal noch steht; Geschwister-Modals).
import { useSyncExternalStore } from 'react'

export type OverlayKind = 'overlay' | 'modal' | 'menu'

const stack: { id: symbol; kind: OverlayKind }[] = []
const listeners = new Set<() => void>()
const notify = (): void => { for (const l of listeners) l() }

/** Overlays, die den Body sperren und eine z-Tiefe belegen (alles außer Menüs). */
const sperrend = (): number => stack.filter(e => e.kind !== 'menu').length

let urspruenglicherOverflow = ''
const body = (): HTMLElement | undefined => (globalThis as { document?: { body?: HTMLElement } }).document?.body

/** Registers an overlay and returns its stacking depth (0 for the first
 *  overlay open at all, 1 for one opened on top of it, ...) — Modal uses this
 *  to lift a Modal opened from inside another Modal (e.g. a delete-confirm
 *  opened from an edit Modal) strictly above the one underneath it.
 *  `kind` defaults to 'overlay' (Drawer); Modal registers as 'modal' so
 *  `useModalOpen` below can tell chrome floating outside the overlay tree
 *  (e.g. the bot launcher) to get out of the way. */
export function pushOverlay(id: symbol, kind: OverlayKind = 'overlay'): number {
  const depth = sperrend()
  if (kind !== 'menu' && depth === 0) {
    const b = body()
    if (b) { urspruenglicherOverflow = b.style.overflow; b.style.overflow = 'hidden' }
  }
  stack.push({ id, kind })
  notify()
  return depth
}
export function popOverlay(id: symbol): void {
  const i = stack.findIndex(e => e.id === id)
  if (i >= 0) {
    const kind = stack[i].kind
    stack.splice(i, 1)
    if (kind !== 'menu' && sperrend() === 0) {
      const b = body()
      if (b) b.style.overflow = urspruenglicherOverflow
    }
  }
  notify()
}
export function isTopOverlay(id: symbol): boolean { return stack.length > 0 && stack[stack.length - 1].id === id }

/** Ein offenes Menü (Dropdown, MultiSelect) meldet sich an und schließt sich beim Escape — aber nur, wenn es OBEN steht;
 *  Drawer und Modal darunter bleiben dabei offen. Der Listener wird nach dem Anmelden registriert, also nach denen der
 *  darunterliegenden Overlays: die sehen das Menü beim selben Tastendruck noch auf dem Stapel. Gibt die Abmeldung zurück. */
export function registerMenuEscape(onEscape: () => void): () => void {
  const id = Symbol('menu')
  pushOverlay(id, 'menu')
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape' && isTopOverlay(id)) onEscape() }
  document.addEventListener('keydown', onKey)
  return () => {
    popOverlay(id)
    document.removeEventListener('keydown', onKey)
  }
}

/** Subscribes to every push/pop; returns the unsubscribe function. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const hasOpenModal = (): boolean => stack.some(e => e.kind === 'modal')

/** True while at least one `kind: 'modal'` overlay is open. */
export function useModalOpen(): boolean {
  return useSyncExternalStore(subscribe, hasOpenModal, hasOpenModal)
}
